import React from 'react';
import { connect } from 'react-redux';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import styles from './PortfolioModalNew.module.scss';
import '@github/relative-time-element';
import PortfolioModalGallery from './PortfolioModalGallery.tsx';
import PortfolioModalQuestions from './PortfolioModalQuestions.tsx';
import ModalOverlay from '../../../UI/ModalOverlay/ModalOverlay.tsx';
import MapPinIcon from '../../../../assets/iconsTsx/MapPinIcon.tsx';
import CalendarIcon from '../../../../assets/iconsTsx/CalendarIcon.tsx';
import ClockIcon from '../../../../assets/iconsTsx/ClockIcon.tsx';

interface PortfolioModalNewProps {
  item: {
    id: string | number;
    name: string;
    description?: string;
    type?: 'producto' | 'servicio';
    price?: string | number;
    priceUnit?: string;
    portfolio_images?: Array<{ image: string }>;
    zone?: string;
    startDate?: string;
    endDate?: string;
    createdAt?: string;
  };
  closeModal: () => void;
  portfolioQuestionsForItem: any[];
  myPerson: boolean;
  userData: any;
}

const modal = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { delay: 0.1 },
  },
};

const PortfolioModalNew: React.FC<PortfolioModalNewProps> = ({
  item,
  closeModal,
  portfolioQuestionsForItem,
  myPerson,
  userData
}) => {
  const formatPrice = (price: string | number): string => {
    if (!price) return '';
    const numPrice = typeof price === 'string' ? parseInt(price, 10) : price;
    return `$ ${new Intl.NumberFormat('es-AR').format(numPrice)}`;
  };

  return (
    <ModalOverlay isOpen={true} onClose={closeModal} zIndex={1000}>
      <motion.div
        className={styles.modal_container}
        variants={modal}
        initial="hidden"
        animate="visible"
        exit="hidden"
      >
        <div className={styles.modal_header}>
          <div className={styles.modal_header_content}>
            <div className={styles.modal_badge}>
              <span>{item.type === 'producto' ? 'Producto' : 'Servicio'}</span>
            </div>
            {item.price && (
              <div className={styles.modal_price}>
                <span>{formatPrice(item.price)}{item.priceUnit ? ` x ${item.priceUnit}` : ''}</span>
              </div>
            )}
          </div>
          <button className={styles.modal_close} onClick={closeModal}>
            <X size={24} />
          </button>
        </div>

        <div className={styles.modal_content}>
          <PortfolioModalGallery images={item.portfolio_images || []} name={item.name} type={item.type} />

          <div className={styles.info_section}>
            <div className={styles.info_content}>
              <h2 className={styles.info_title}>{item.name}</h2>

              {item.description && (
                <p className={styles.info_description}>{item.description}</p>
              )}

              {(item.zone || item.startDate || item.endDate || item.createdAt) && (
                <div className={styles.info_details}>
                  {item.zone && (
                    <div className={styles.info_detail}>
                      <MapPinIcon className={styles.info_detail_icon} />
                      <span>{item.zone}</span>
                    </div>
                  )}

                  {(item.startDate || item.endDate) && (
                    <div className={styles.info_detail}>
                      <CalendarIcon className={styles.info_detail_icon} />
                      <span>
                        {item.startDate && new Date(item.startDate).toLocaleDateString('es-ES')}
                        {item.startDate && item.endDate && ' - '}
                        {item.endDate && new Date(item.endDate).toLocaleDateString('es-ES')}
                      </span>
                    </div>
                  )}

                  {item.createdAt && (
                    <div className={styles.info_detail}>
                      <ClockIcon className={styles.info_detail_icon} />
                      <span>
                        <relative-time lang="es" tense="past" datetime={item.createdAt}></relative-time>
                      </span>
                    </div>
                  )}
                </div>
              )}

              <PortfolioModalQuestions
                questions={portfolioQuestionsForItem}
                myPerson={myPerson}
                portfolioId={item.id}
                userData={userData}
              />
            </div>
          </div>
        </div>
      </motion.div>
    </ModalOverlay>
  );
};

const mapStateToProps = (state: any) => ({
  portfolioQuestions: state.profile.portfolioQuestions,
});

export default connect(mapStateToProps, {})(PortfolioModalNew);

